/**
 * Loaders pour les fichiers JSON de `public/data/`.
 * Chaque loader fetch le JSON puis le valide via Zod. Cache en mémoire
 * (via `once`) : un seul fetch + une seule validation par exécution.
 *
 * 10 loaders runtime exposes (BDD core consommee par l'app : profils, peuples,
 * voies, equipement, creatures, objets-magiques, regles, pantheon, glossaire).
 *
 * 2 collections supplementaires generees par build-bdd mais NON chargees par
 * loadBDD() : compagnons.json + religions.json. Validees a la build/CI mais
 * pas consommees au runtime tant que l'app M3+ ne les exploite pas.
 */

import type { ZodTypeAny, z } from 'zod';
import type {
  CreaturesFile,
  EquipementFile,
  GlossaireFile,
  ObjetsMagiquesFile,
  PantheonFile,
  PeuplesFile,
  ProfilsFile,
  ReglesFile,
  VoiesClasseFile,
  VoiesPrestigeFile,
} from './schemas';

/**
 * Mémoïse un loader asynchrone : un seul appel effectif en cas de succès.
 * En cas d'échec, le cache est réinitialisé pour permettre une nouvelle tentative.
 */
function once<T>(fn: () => Promise<T>): () => Promise<T> {
  let cache: Promise<T> | null = null;
  return () => {
    if (cache === null) {
      cache = fn().catch((e) => {
        cache = null;
        return Promise.reject(e);
      });
    }
    return cache;
  };
}

/**
 * Fetch + parse un fichier JSON de BDD.
 * La validation Zod est effectuee au build (npm run validate:bdd),
 * pas au runtime - gain de plusieurs secondes au chargement.
 */
export async function loadJSON<T>(path: string): Promise<T> {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error(`Impossible de charger ${path} : HTTP ${response.status}`);
  }
  return response.json() as Promise<T>;
}

/**
 * Fetch + parse + valide un fichier JSON de BDD.
 * Lance une Error explicite si le JSON est malformé ou invalide.
 * Utilise uniquement en CI/tests - en runtime, utiliser loadJSON.
 */
export async function loadAndValidate<S extends ZodTypeAny>(
  path: string,
  schema: S,
): Promise<z.infer<S>> {
  let raw: unknown;
  try {
    const response = await fetch(path);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status} ${response.statusText}`);
    }
    raw = await response.json();
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new Error(`Impossible de charger ${path} : ${msg}`);
  }

  const result = schema.safeParse(raw);
  if (!result.success) {
    const issues = result.error.issues
      .slice(0, 10)
      .map((i) => `  • ${i.path.join('.') || '<racine>'} : ${i.message}`)
      .join('\n');
    throw new Error(`BDD invalide (${path}) :\n${issues}`);
  }
  return result.data as z.infer<S>;
}

const DATA_BASE = 'data';

export const loadProfils = once<ProfilsFile>(() => loadJSON(`${DATA_BASE}/profils.json`));

export const loadPeuples = once<PeuplesFile>(() => loadJSON(`${DATA_BASE}/peuples.json`));

export const loadVoiesClasse = once<VoiesClasseFile>(() =>
  loadJSON(`${DATA_BASE}/voies_classe.json`),
);

export const loadVoiesPrestige = once<VoiesPrestigeFile>(() =>
  loadJSON(`${DATA_BASE}/voies_prestige.json`),
);

export const loadEquipement = once<EquipementFile>(() => loadJSON(`${DATA_BASE}/equipement.json`));

export const loadCreatures = once<CreaturesFile>(() => loadJSON(`${DATA_BASE}/creatures.json`));

export const loadObjetsMagiques = once<ObjetsMagiquesFile>(() =>
  loadJSON(`${DATA_BASE}/objets_magiques.json`),
);

export const loadRegles = once<ReglesFile>(() => loadJSON(`${DATA_BASE}/regles.json`));

export const loadPantheon = once<PantheonFile>(() => loadJSON(`${DATA_BASE}/pantheon.json`));

export const loadGlossaire = once<GlossaireFile>(() => loadJSON(`${DATA_BASE}/glossaire.json`));

export interface CompagnonBase {
  id: string;
  nom: string;
  type: string;
  creature_base_id: string;
  voie_source_id: string;
  rang_acquisition: number;
  persistance: string;
  peut_combattre: boolean;
  initiative_regles: string;
  controle_par: string;
  stats_base: {
    pv: string;
    def: string;
    att: string;
    dm: string;
    init: string;
    modificateurs: Record<string, number>;
  };
  upgrades?: unknown[];
}

export interface CompagnonsFile {
  compagnons: CompagnonBase[];
  familiers: unknown[];
  invocations: unknown[];
}

export const loadCompagnons = once<CompagnonsFile>(() => loadJSON(`${DATA_BASE}/compagnons.json`));

/**
 * Charge l'ensemble de la BDD en parallèle.
 * Lance une erreur agrégée si un ou plusieurs fichiers échouent.
 */
export interface BDD {
  profils: ProfilsFile;
  peuples: PeuplesFile;
  voiesClasse: VoiesClasseFile;
  voiesPrestige: VoiesPrestigeFile;
  equipement: EquipementFile;
  creatures: CreaturesFile;
  objetsMagiques: ObjetsMagiquesFile;
  regles: ReglesFile;
  pantheon: PantheonFile;
  glossaire: GlossaireFile;
}

export async function loadBDD(): Promise<BDD> {
  const [
    profils,
    peuples,
    voiesClasse,
    voiesPrestige,
    equipement,
    creatures,
    objetsMagiques,
    regles,
    pantheon,
    glossaire,
  ] = await Promise.all([
    loadProfils(),
    loadPeuples(),
    loadVoiesClasse(),
    loadVoiesPrestige(),
    loadEquipement(),
    loadCreatures(),
    loadObjetsMagiques(),
    loadRegles(),
    loadPantheon(),
    loadGlossaire(),
  ]);
  return {
    profils,
    peuples,
    voiesClasse,
    voiesPrestige,
    equipement,
    creatures,
    objetsMagiques,
    regles,
    pantheon,
    glossaire,
  };
}
